<template>
  <Dialog
    v-model:visible="isVisible"
    :header="header"
    :modal="true"
    :style="{ width: '95vw', maxWidth: '500px' }"
    :draggable="false"
    :closable="!isLoading"
    :closeOnEscape="!isLoading"
    :data-test-id="dataTestId"
  >
    <div class="flex items-start space-x-3">
      <div class="flex-shrink-0">
        <i class="pi pi-exclamation-triangle text-2xl text-red-500"></i>
      </div>
      <div class="flex-1">
        <slot>
          <p
            v-if="message"
            class="text-surface-900 dark:text-surface-0 font-medium mb-2"
          >
            {{ message }}
          </p>
        </slot>

        <div class="mt-4">
          <label
            :for="`${dataTestId}-input`"
            class="block text-sm text-surface-700 dark:text-surface-300 mb-2"
          >
            Type
            <strong class="font-semibold">{{ requiredText }}</strong>
            to confirm
          </label>
          <InputText
            :id="`${dataTestId}-input`"
            v-model="confirmInput"
            :placeholder="requiredText"
            class="w-full"
            :data-test-id="`${dataTestId}-input`"
            @keydown.enter="handleEnterKey"
          />
        </div>
      </div>
    </div>

    <template #footer>
      <Button
        :label="cancelLabel"
        icon="pi pi-times"
        @click="handleCancel"
        variant="outlined"
        :disabled="isLoading"
        :data-test-id="`${dataTestId}-cancel-btn`"
      />
      <Button
        :label="confirmLabel"
        :icon="confirmIcon"
        @click="handleConfirm"
        :severity="severity"
        :loading="isLoading"
        :disabled="!isConfirmEnabled"
        :data-test-id="`${dataTestId}-confirm-btn`"
      />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { Dialog, Button, InputText } from 'primevue'

interface Props {
  visible: boolean
  header: string
  requiredText: string
  message?: string
  confirmLabel?: string
  confirmIcon?: string
  cancelLabel?: string
  severity?: string
  isLoading?: boolean
  dataTestId?: string
}

const props = withDefaults(defineProps<Props>(), {
  message: '',
  confirmLabel: 'Confirm',
  confirmIcon: 'pi pi-check',
  cancelLabel: 'Cancel',
  severity: 'danger',
  isLoading: false,
  dataTestId: 'text-confirm-dialog',
})

const emit = defineEmits<{
  'update:visible': [value: boolean]
  confirm: []
  cancel: []
}>()

const isVisible = ref(props.visible)
const confirmInput = ref('')

const isConfirmEnabled = computed(() => {
  const hasRequiredText = props.requiredText.trim().length > 0
  return hasRequiredText && confirmInput.value === props.requiredText && !props.isLoading
})

watch(
  () => props.visible,
  newValue => {
    isVisible.value = newValue
    if (newValue) {
      confirmInput.value = ''
    }
  },
)

watch(isVisible, newValue => {
  emit('update:visible', newValue)
  if (!newValue) {
    confirmInput.value = ''
  }
})

function handleConfirm() {
  if (isConfirmEnabled.value) {
    emit('confirm')
  }
}

function handleCancel() {
  isVisible.value = false
  emit('cancel')
}

function handleEnterKey() {
  if (isConfirmEnabled.value) {
    handleConfirm()
  }
}
</script>
